import Button from '../ui/Button'
import ReviewCard, { type ReviewCardData } from './ReviewCard'
import RatingBreakdown, { type RatingBreakdownBucket } from './RatingBreakdown'

export type ReviewSortOption = 'RECENT' | 'HIGHEST_RATING' | 'LOWEST_RATING'

interface ReviewsListCopy {
  reviewsBody: string
  reviewsLabel: string
  noReviewsTitle: string
  noReviewsBody: string
  reviewPlaceholder: string
  sortLabel: string
  sortRecent: string
  sortHighest: string
  sortLowest: string
  loadMore: string
}

export default function ReviewsList({
  averageRating,
  totalReviews,
  breakdown,
  reviews,
  loading,
  hasNextPage,
  sortBy,
  onSortChange,
  onLoadMore,
  copy,
}: {
  averageRating: number
  totalReviews: number
  breakdown: RatingBreakdownBucket[]
  reviews: ReviewCardData[]
  loading: boolean
  hasNextPage: boolean
  sortBy: ReviewSortOption
  onSortChange: (nextValue: ReviewSortOption) => void
  onLoadMore: () => void
  copy: ReviewsListCopy
}) {
  if (reviews.length === 0) {
    return (
      <div className="mt-6 rounded-[1.5rem] border border-slate-200 bg-slate-50 p-6">
        <h3 className="text-lg font-bold text-slate-950">{copy.noReviewsTitle}</h3>
        <p className="mt-3 text-sm leading-7 text-slate-600">{copy.noReviewsBody}</p>
      </div>
    )
  }

  return (
    <div className="mt-6 space-y-6">
      <RatingBreakdown
        averageRating={averageRating}
        totalReviews={totalReviews}
        buckets={breakdown}
        reviewsLabel={copy.reviewsLabel}
      />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="max-w-2xl text-sm leading-7 text-slate-600">{copy.reviewsBody}</p>
        <label className="flex items-center gap-3 text-sm font-medium text-slate-600">
          <span>{copy.sortLabel}</span>
          <select
            value={sortBy}
            onChange={(event) => onSortChange(event.target.value as ReviewSortOption)}
            className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="RECENT">{copy.sortRecent}</option>
            <option value="HIGHEST_RATING">{copy.sortHighest}</option>
            <option value="LOWEST_RATING">{copy.sortLowest}</option>
          </select>
        </label>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {reviews.map((review) => (
          <ReviewCard key={review.id} review={review} placeholder={copy.reviewPlaceholder} />
        ))}
      </div>

      {hasNextPage && (
        <div className="flex justify-center">
          <Button type="button" variant="secondary" loading={loading} onClick={onLoadMore}>
            {copy.loadMore}
          </Button>
        </div>
      )}
    </div>
  )
}
